import * as React from 'react'
import { useEffect, useReducer, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import { Form, Input, Select } from 'antd'
import { PageAnalysisFieldStrategyType } from '@pushly/aqe/lib/enums/page-analysis-field-strategy-type'
import { Drawer } from '@pushly/aqe/lib/components'
import { IPageAnalysisFieldStrategy } from '@pushly/aqe/lib/interfaces'
import { DataType, Delimiter, EmbeddedJsonSource } from '@pushly/aqe/lib/enums'

export interface IEditFieldConfigStrategyParams {
    fieldConfigStrategy: Partial<IPageAnalysisFieldStrategy>
    pageAnalysisFieldType: DataType
    ordinal?: number
}

export interface IEditFieldConfigStrategyDrawerProps {
    params: IEditFieldConfigStrategyParams
    onClose: () => void
    onUpdate: (fieldConfigStrategy: IPageAnalysisFieldStrategy, ordinal?: number) => void
}

function EditFieldConfigStrategyDrawer(props: IEditFieldConfigStrategyDrawerProps) {
    const appService = Container.get(AppService)
    const [fieldConfigStrategy] = useState(props.params?.fieldConfigStrategy)
    const [form] = Form.useForm<IPageAnalysisFieldStrategy>()
    const [_, refresh] = useReducer((x) => x + 1, 0)

    useEffect(() => {
        form.setFieldsValue({
            type: null,
            config: null,
            ...props.params?.fieldConfigStrategy,
        })
        refresh()
    }, [props.params?.fieldConfigStrategy])

    function render() {
        return (
            <Drawer
                width={800}
                getContainer={appService.getAppContainer}
                className="edit-field-config-drawer"
                title={props.params?.ordinal != null ? 'Edit Field Config Strategy' : 'Add Field Config Strategy'}
                placement="right"
                closable={true}
                submitText={
                    props.params?.ordinal != null ? 'Update Field Config Strategy' : 'Add Field Config Strategy'
                }
                onSubmit={() => {
                    form.submit()
                }}
                onClose={props.onClose}
                open={!!props.params?.fieldConfigStrategy}
            >
                {props.params?.fieldConfigStrategy && (
                    <Form
                        layout="horizontal"
                        form={form}
                        onFinish={() => {
                            props.onUpdate(form.getFieldsValue() as IPageAnalysisFieldStrategy, props.params.ordinal)
                        }}
                        size="small"
                        labelAlign="left"
                        requiredMark={false}
                    >
                        <Form.Item label="Type" name="type" rules={[{ required: true, message: 'Type is required' }]}>
                            <Select
                                onChange={(e: any) => {
                                    refresh()
                                }}
                            >
                                <Select.Option
                                    key={PageAnalysisFieldStrategyType.XPATH}
                                    value={PageAnalysisFieldStrategyType.XPATH}
                                >
                                    xpath
                                </Select.Option>
                                <Select.Option
                                    key={PageAnalysisFieldStrategyType.JSONLD}
                                    value={PageAnalysisFieldStrategyType.JSONLD}
                                >
                                    jsonld
                                </Select.Option>
                                <Select.Option
                                    key={PageAnalysisFieldStrategyType.JSONPATH}
                                    value={PageAnalysisFieldStrategyType.JSONPATH}
                                >
                                    jsonpath
                                </Select.Option>
                                <Select.Option
                                    key={PageAnalysisFieldStrategyType.EMBEDDED_JSON}
                                    value={PageAnalysisFieldStrategyType.EMBEDDED_JSON}
                                >
                                    embedded_json
                                </Select.Option>
                            </Select>
                        </Form.Item>

                        {form.getFieldValue('type') === PageAnalysisFieldStrategyType.XPATH && (
                            <Form.Item
                                label="XPath"
                                name={['config', 'xpath']}
                                rules={[{ required: true, message: 'XPath is required' }]}
                            >
                                <Input autoComplete="off" />
                            </Form.Item>
                        )}

                        {form.getFieldValue('type') === PageAnalysisFieldStrategyType.XPATH &&
                            [
                                DataType.LINT,
                                DataType.LBOOL,
                                DataType.LSTRING,
                                DataType.LFLOAT,
                                DataType.LTIMESTAMP,
                                DataType.LURL,
                            ].includes(props.params?.pageAnalysisFieldType) && (
                                <Form.Item label="Delimiter" name={['config', 'delimiter']}>
                                    <Select
                                        onChange={(e: any) => {
                                            refresh()
                                        }}
                                        defaultValue={Delimiter.COMMA}
                                    >
                                        <Select.Option key={Delimiter.COMMA} value={Delimiter.COMMA}>
                                            Comma
                                        </Select.Option>
                                        <Select.Option key={Delimiter.SEMICOLON} value={Delimiter.SEMICOLON}>
                                            Semicolon
                                        </Select.Option>
                                        <Select.Option key={Delimiter.PIPE} value={Delimiter.PIPE}>
                                            Pipe
                                        </Select.Option>
                                        <Select.Option key={Delimiter.SLASH} value={Delimiter.SLASH}>
                                            Slash
                                        </Select.Option>
                                    </Select>
                                </Form.Item>
                            )}

                        {form.getFieldValue('type') === PageAnalysisFieldStrategyType.JSONLD && (
                            <>
                                <Form.Item
                                    label="Schema Type"
                                    name={['config', 'schema_type']}
                                    rules={[{ required: true, message: 'Schema Type is required' }]}
                                >
                                    <Input autoComplete="off" />
                                </Form.Item>

                                <Form.Item
                                    label="Json Path"
                                    name={['config', 'json_path']}
                                    rules={[{ required: true, message: 'Json Path is required' }]}
                                >
                                    <Input autoComplete="off" />
                                </Form.Item>
                            </>
                        )}

                        {form.getFieldValue('type') === PageAnalysisFieldStrategyType.JSONPATH && (
                            <>
                                <Form.Item
                                    label="HTML Element ID"
                                    name={['config', 'element_id']}
                                    rules={[{ required: true, message: 'HTML Element ID is required' }]}
                                >
                                    <Input autoComplete="off" />
                                </Form.Item>

                                <Form.Item
                                    label="Json Path"
                                    name={['config', 'json_path']}
                                    rules={[{ required: true, message: 'Json Path is required' }]}
                                >
                                    <Input autoComplete="off" />
                                </Form.Item>
                            </>
                        )}

                        {form.getFieldValue('type') === PageAnalysisFieldStrategyType.EMBEDDED_JSON && (
                            <>
                                <Form.Item
                                    label="Source"
                                    name={['config', 'source']}
                                    rules={[{ required: true, message: 'Source is required' }]}
                                >
                                    <Select
                                        onChange={(e: any) => {
                                            refresh()
                                        }}
                                    >
                                        <Select.Option
                                            key={EmbeddedJsonSource.NEXT_FLIGHT}
                                            value={EmbeddedJsonSource.NEXT_FLIGHT}
                                        >
                                            next_flight
                                        </Select.Option>
                                        <Select.Option
                                            key={EmbeddedJsonSource.SCRIPT_BY_ID}
                                            value={EmbeddedJsonSource.SCRIPT_BY_ID}
                                        >
                                            script_by_id
                                        </Select.Option>
                                        <Select.Option
                                            key={EmbeddedJsonSource.LD_JSON}
                                            value={EmbeddedJsonSource.LD_JSON}
                                        >
                                            ld_json
                                        </Select.Option>
                                    </Select>
                                </Form.Item>

                                {form.getFieldValue('config')?.source === EmbeddedJsonSource.SCRIPT_BY_ID && (
                                    <Form.Item
                                        label="HTML Element ID"
                                        name={['config', 'element_id']}
                                        rules={[{ required: true, message: 'HTML Element ID is required' }]}
                                    >
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                )}

                                {form.getFieldValue('config')?.source === EmbeddedJsonSource.LD_JSON && (
                                    <Form.Item
                                        label="Schema Type"
                                        name={['config', 'schema_type']}
                                        rules={[{ required: true, message: 'Schema Type is required' }]}
                                    >
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                )}

                                <Form.Item
                                    label="Json Path"
                                    name={['config', 'json_path']}
                                    rules={[{ required: true, message: 'Json Path is required' }]}
                                >
                                    <Input autoComplete="off" />
                                </Form.Item>

                                {[
                                    DataType.LINT,
                                    DataType.LBOOL,
                                    DataType.LSTRING,
                                    DataType.LFLOAT,
                                    DataType.LTIMESTAMP,
                                    DataType.LURL,
                                ].includes(props.params?.pageAnalysisFieldType) && (
                                    <Form.Item label="Delimiter" name={['config', 'delimiter']}>
                                        <Select
                                            onChange={(e: any) => {
                                                refresh()
                                            }}
                                            defaultValue={Delimiter.COMMA}
                                        >
                                            <Select.Option key={Delimiter.COMMA} value={Delimiter.COMMA}>
                                                Comma
                                            </Select.Option>
                                            <Select.Option key={Delimiter.SEMICOLON} value={Delimiter.SEMICOLON}>
                                                Semicolon
                                            </Select.Option>
                                            <Select.Option key={Delimiter.PIPE} value={Delimiter.PIPE}>
                                                Pipe
                                            </Select.Option>
                                            <Select.Option key={Delimiter.SLASH} value={Delimiter.SLASH}>
                                                Slash
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>
                                )}
                            </>
                        )}
                    </Form>
                )}
            </Drawer>
        )
    }

    return render()
}

export default EditFieldConfigStrategyDrawer
